import { fetchConfig } from "@theme/utilities";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const REQUIRED_UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign"];
const SEARCH_ENGINE_HOSTS = [
  "google.",
  "yahoo.",
  "bing.com",
  "duckduckgo.com",
  "yandex.",
  "baidu.com",
];
const SESSION_KEY = "utm-cart-attributes-synced";

/**
 * Reads utm_* parameters from the current URL's query string.
 * @returns {Record<string, string>}
 */
function getUtmParamsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  /** @type {Record<string, string>} */
  const utmParams = {};

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) utmParams[key] = value;
  }

  return utmParams;
}

/**
 * @returns {string} The referrer's hostname, or an empty string if there is none or it's invalid.
 */
function getReferrerHostname() {
  if (!document.referrer) return "";

  try {
    return new URL(document.referrer).hostname;
  } catch {
    return "";
  }
}

/**
 * Determines the traffic source status:
 * - 'error': a utm-tagged link is missing one of the required parameters (source/medium/campaign).
 * - 'organic_search': there's no utm parameter and the referrer is a known search engine.
 * @param {Record<string, string>} utmParams
 * @returns {'error' | 'organic_search' | null}
 */
function getTrafficSourceStatus(utmParams) {
  if (Object.keys(utmParams).length > 0) {
    const isMissingRequiredParam = REQUIRED_UTM_PARAMS.some(
      (key) => !utmParams[key],
    );
    return isMissingRequiredParam ? "error" : null;
  }

  const referrerHostname = getReferrerHostname();
  const isSearchEngineReferrer = SEARCH_ENGINE_HOSTS.some((host) =>
    referrerHostname.includes(host),
  );

  return isSearchEngineReferrer ? "organic_search" : null;
}

/**
 * Persists utm_* parameters and the traffic source status onto the cart's attributes via the
 * Ajax Cart API. `/cart/update.js` replaces the entire attributes object, so the current
 * cart attributes are fetched first and merged with the new values to avoid clearing unrelated
 * attributes.
 * @returns {Promise<void>}
 */
async function syncUtmCartAttributes() {
  const utmParams = getUtmParamsFromUrl();
  const trafficSourceStatus = getTrafficSourceStatus(utmParams);

  if (Object.keys(utmParams).length === 0 && !trafficSourceStatus) return;

  const syncKey = JSON.stringify({ utmParams, trafficSourceStatus });
  if (sessionStorage.getItem(SESSION_KEY) === syncKey) return;

  const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
  const cart = await cartResponse.json();
  /** @type {Record<string, string>} */
  const attributes = { ...cart.attributes, ...utmParams };

  // 空文字を送ると Shopify 側で既存の属性値が削除されるため、該当しない場合は明示的にクリアする
  attributes.traffic_source_status = trafficSourceStatus || "";

  await fetch(
    Theme.routes.cart_update_url,
    fetchConfig("json", { body: JSON.stringify({ attributes }) }),
  );

  sessionStorage.setItem(SESSION_KEY, syncKey);
}

syncUtmCartAttributes();
